import qconsole from 'scripts/lib/qconsole';

import TeamAssistMessage from 'models/team_assist_message';

export default class TeamAssistGatewayObserver {
  constructor(context) {
    this.context = context;
    this._shownTracked = new Set();
  }

  getStore(customerId) {
    if (this._isCustomerLoaded(customerId)) {
      return this.context.stores.customers.storesFor(customerId).teamAssists;
    }
    qconsole.log('TeamAssistGatewayObserver: customer not loaded. Ignoring response');
    return null;
  }

  onBroadcast(dto) {
    const store = this.getStore(dto.customerId);
    if (store) {
      store.addOrReplace(TeamAssistMessage.fromJs(dto));
      this._trackIfSuggestedResponse(dto);
    }
  }

  onFetchAllSuccess(fetchResultDto, { customerId }) {
    const store = this.getStore(customerId);
    if (store) {
      store.resetLoading();
      store.set(fetchResultDto.map(dto => TeamAssistMessage.fromJs(dto)));
    }
  }

  onFetchAllError(err, { customerId }) {
    const store = this.getStore(customerId);
    if (store) {
      store.resetLoading();
    }
    qconsole.log(`TeamAssistGatewayObserver: failed to fetch team assists: ${JSON.stringify(err)}`);
  }

  onAddSuccess(dto, { customerId }) {
    const store = this.getStore(customerId);
    if (store) {
      if (dto.messages) {
        dto.messages.forEach(msg => {
          store.addOrReplace(TeamAssistMessage.fromJs(msg));
          this._trackIfSuggestedResponse(msg);
        });
      } else {
        store.addOrReplace(TeamAssistMessage.fromJs(dto));
        this._trackIfSuggestedResponse(dto);
      }
    }
  }

  onAddError(err, { customerId }) {
    qconsole.log(`TeamAssistGatewayObserver: failed to add team assist message: ${JSON.stringify(err)}`);
  }

  onBroadcastDelete(dto, { customerId }) {
    const store = this.getStore(customerId);
    if (store) {
      store.set([]);
    }
  }

  onDeleteSuccess(dto, { customerId }) {
    const store = this.getStore(customerId);
    if (store) {
      store.set([]);
    }
  }

  onDeleteError(err, { customerId }) {
    qconsole.log(`TeamAssistGatewayObserver: failed to clear team assist session: ${JSON.stringify(err)}`);
  }

  onUpdateSuccess(dto, { customerId }) {
    const store = this.getStore(customerId);
    if (store) {
      store.addOrReplace(TeamAssistMessage.fromJs(dto));
    }
  }

  onUpdateError(err, { customerId, teamAssistId }) {
    qconsole.log(
      `TeamAssistGatewayObserver: failed to update team assist message [${teamAssistId}] for customer [${customerId}]:`,
      err
    );
  }

  onRetryError(err, { customerId, messageId }) {
    qconsole.log(
      `TeamAssistGatewayObserver: failed to retry team assist message [${messageId}] for customer [${customerId}]:`,
      err
    );
    const store = this.getStore(customerId);
    if (store) {
      try {
        const message = store.find(messageId);
        if (message) {
          store.addOrReplace(
            TeamAssistMessage.fromJs({
              ...message.toJs(),
              isRetrying: false,
            })
          );
        }
      } catch (e) {
        // message may already be gone
      }
    }
  }

  onRetrySuccess(dto, { customerId }) {
    const store = this.getStore(customerId);
    if (store) {
      store.addOrReplace(TeamAssistMessage.fromJs(dto));
    }
  }

  _isCustomerLoaded(customerId) {
    return this.context.stores.customers.has({ id: customerId });
  }

  // Fires the FullStory 'Shown' event when a suggested_response ai_response
  // first arrives from the server (POST add or MQTT broadcast). Lives here
  // rather than on the message component's mount so re-opening or
  // re-rendering the panel does not double-count a single suggestion.
  _trackIfSuggestedResponse(dto) {
    if (!dto || dto.role !== 'ai_response' || dto.type !== 'suggested_response') {
      return;
    }
    if (this._shownTracked.has(dto.id)) {
      return;
    }
    this._shownTracked.add(dto.id);
    this.context.analytics.track('Team Assist Suggested Response Shown', {
      customerId: dto.customerId,
      sessionId: dto.sessionId,
      messageId: dto.id,
    });
  }
}
